/* ═══════════════════════════════════════════════════════════════════════════
   THE JOURNAL — what an accountant may actually have

   Revenue per day, split by VAT rate, in the shape a bookkeeping system
   takes it. Built on `domain/revenue.js`, which already knows the one thing
   that matters most here: agreed, held and taken are three different numbers
   and only one of them is revenue.

   ── The rule this file exists to enforce ─────────────────────────────────

   ONLY WHAT WAS TAKEN IS EXPORTED. Not what was agreed, not what is held
   against a card. An appointment next Thursday with a price on it is not
   income, and a journal that exported it would have a business declaring VAT
   on money it has not received and may never receive.

   In this build nothing is ever captured — there are no merchant keys — so
   the honest export is an empty one, and the screen says exactly that
   instead of quietly exporting the agreed figures because an empty file
   looks broken. That is the whole point of the distinction: the day Stripe
   is wired up, the same code starts producing a real journal and nothing
   about what it means has changed.

   ── The rate is never guessed ────────────────────────────────────────────

   Norway charges 25 % on most services, 15 % on food to take away, 12 % on a
   handful of things and nothing at all on health and teaching — and a
   restaurant charges 25 % on the very same dish it charges 15 % on in a box.
   A trade code cannot answer that. So a service with no rate set is REPORTED
   AND EXCLUDED rather than exported at a guess: `unrated` comes back with
   the list, the screen shows it, and the export refuses until somebody has
   answered.

   A guessed rate is worse than a missing one, because a missing one gets
   noticed.

   ── Gross, net and the VAT ───────────────────────────────────────────────

   A price in Norway is quoted INCLUDING VAT. So the amount on a booking is
   gross, and the tax inside it is `gross × pct / (100 + pct)` — not
   `gross × pct`, which is the error that overstates the VAT on a 25 % line
   by a quarter and is the single most common mistake in a hand-rolled
   journal.
   ═══════════════════════════════════════════════════════════════════════ */
import { rules as rulesFor, HOME } from '../law/index.js';
import { moneyOf, counts } from './revenue.js';

const round2 = n => Math.round(Number(n || 0) * 100) / 100;

/** The rates this country has, as the screens offer them. */
export const rates = (country = HOME) => rulesFor(country).vat.rates;

export const rateOf = (id, country = HOME) =>
  rates(country).find(r => r.id === id) || null;

/**
 * The VAT inside a gross amount.
 *
 * Prices here are quoted including VAT, so the tax is the part of the gross
 * rather than a percentage added to it. `gross × pct / (100 + pct)`, and the
 * comment is here because `gross × pct / 100` looks right and is not.
 */
export function splitGross(gross, pct) {
  const g = Number(gross) || 0;
  const p = Number(pct) || 0;
  const vat = round2((g * p) / (100 + p));
  return { gross: round2(g), vat, net: round2(g - vat) };
}

/**
 * One day's journal, by VAT rate.
 *
 * `rateFor` is a function from a row to a rate id — the caller supplies it,
 * because the mapping from a service to a rate lives on the business's own
 * price list and not in this module.
 *
 * A row whose rate is unknown does not get a line. It goes in `unrated`, and
 * the total says so.
 */
export function journalFor(rows = [], rateFor, country = HOME) {
  const byRate = {};
  const unrated = [];
  let taken = 0;

  rows.filter(counts).forEach(r => {
    const m = moneyOf(r);
    /* Only what has actually been captured. See the note at the top: an
       appointment with a price on it is not income. */
    if (!m.taken) return;
    taken += m.taken;

    const id = rateFor ? rateFor(r) : null;
    const rate = id ? rateOf(id, country) : null;
    if (!rate) {
      unrated.push({ service: r.service || '(unnamed)', gross: m.taken });
      return;
    }
    const part = splitGross(m.taken, rate.pct);
    const line = byRate[rate.id] || (byRate[rate.id] = {
      id: rate.id, pct: rate.pct, label: rate.label,
      gross: 0, net: 0, vat: 0, count: 0,
    });
    line.gross = round2(line.gross + part.gross);
    line.net = round2(line.net + part.net);
    line.vat = round2(line.vat + part.vat);
    line.count += 1;
  });

  const lines = Object.values(byRate).sort((a, b) => b.pct - a.pct);
  const unratedGross = round2(unrated.reduce((n, u) => n + u.gross, 0));

  return {
    lines,
    unrated,
    unratedGross,
    gross: round2(lines.reduce((n, l) => n + l.gross, 0)),
    net: round2(lines.reduce((n, l) => n + l.net, 0)),
    vat: round2(lines.reduce((n, l) => n + l.vat, 0)),
    /* Everything captured, whether or not it could be rated. The gap between
       this and `gross` is exactly what is missing a rate, and a screen that
       showed only one of the two would be hiding it. */
    taken: round2(taken),
    complete: unrated.length === 0,
  };
}

/**
 * Grouped by day, oldest first, for a range that is already filtered.
 *
 * Only days that produced something. A day whose every booking was agreed
 * and never captured is NOT a day with a journal entry — it grouped, it
 * produced no lines, and returning it made the screen say "14 days with
 * entries" over an empty table and offer a download of a file with nothing
 * in it but a header. The count of days is the count of days an accountant
 * would recognise.
 */
export function journalByDay(rows = [], rateFor, country = HOME) {
  const days = {};
  rows.forEach(r => {
    const d = String(r.time || '').slice(0, 10);
    if (!d) return;
    (days[d] = days[d] || []).push(r);
  });
  return Object.keys(days).sort()
    .map(date => ({ date, ...journalFor(days[date], rateFor, country) }))
    .filter(d => d.lines.length || d.unrated.length);
}

/* ── The export ──────────────────────────────────────────────────────────
   CSV first, because every accounting system in Norway will take one and it
   can be read by a person before it is trusted by a machine. SAF-T is the
   format the Skatteetaten asks for on demand, and it is a bigger job than a
   column layout — it needs the business's chart of accounts, which this
   platform does not hold. So `saftReady` answers what is still missing
   rather than producing an almost-right file. */

const csvCell = v => {
  const s = String(v ?? '');
  return /[",;\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

/**
 * A daily revenue journal as CSV.
 *
 * Semicolons, because Norwegian Excel reads a comma as a decimal separator
 * and a comma-delimited file opens as one column — which is how a correct
 * export gets reported as broken.
 */
export function toCSV(days = [], { business = '', country = HOME } = {}) {
  const head = ['Date', 'VAT rate', 'Rate %', 'Gross', 'Net', 'VAT', 'Appointments', 'Business'];
  const rows = [];
  days.forEach(d => d.lines.forEach(l => {
    rows.push([d.date, l.label, l.pct, l.gross.toFixed(2), l.net.toFixed(2),
               l.vat.toFixed(2), l.count, business]);
  }));
  return [head, ...rows].map(r => r.map(csvCell).join(';')).join('\n');
}

/** What SAF-T would still need. Answered rather than guessed at. */
export function saftReady({ orgNo = null, days = [] } = {}) {
  const missing = [];
  if (!orgNo) missing.push('the organisation number');
  missing.push('a chart of accounts — which account each rate posts to');
  missing.push('the accounting period the file covers');
  if (!days.some(d => d.lines.length)) missing.push('at least one captured payment');
  return {
    ready: false,
    missing,
    what: 'SAF-T is the file Skatteetaten asks for on demand, and it carries '
      + 'the business\'s own chart of accounts. This platform does not hold '
      + 'one, so it will not produce a file that looks like SAF-T and is not. '
      + 'The CSV above is real and complete for what it covers.',
  };
}

/** What the screen says about an export that would be empty, and why. */
export const emptyWhy = () =>
  'Nothing has been captured, so there is nothing an accountant may have. '
  + 'The amounts on the bookings are agreed, not received — exporting those '
  + 'would have you declaring VAT on money that has not arrived.';
